import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import { IOperationResultNoValue, Industry, TemplateStatus } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { makeObservable, observable } from 'mobx';
import { Observer, inject, observer } from 'mobx-react';
import { parse as queryStringToObject } from 'query-string';
import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import {
	DefaultSupportedIndustries,
	ILocationState,
	IModalContext,
	ModalChildComponentContextKey,
} from '../../../../models';
import { ISystemAutomationTemplate } from '../../../../models/AdminModels';
import { ErrorMessagesViewModelKey, IErrorMessageComponentProps } from '../../../../models/AppState';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { IndustryAutomationTemplateViewModel } from '../../../../viewmodels/AdminViewModels';
import { ISelectOption } from '../../../../web/components/DeprecatedSelect';
import {
	INotificationServiceComponentProps,
	withNotificationService,
} from '../../../../web/components/LocalNotificationObserver/WithNotificationService';
import { EditAutomationTemplate } from '../../../../web/containers/EditAutomationTemplate';
import { INavigationItemProps } from '../../../../web/containers/MainContainer';
import { baseStyleSheet } from '../../../../web/styles/styles';
import {
	DefaultIndustryDropdownOptions,
	IndustriesDropdown,
	TemplateIndustryOption,
} from '../../../components/IndustriesDropdown';
import { styleSheet } from './styles';

interface IProps
	extends IEventLoggingComponentProps,
		RouteComponentProps<any>,
		INavigationItemProps,
		IErrorMessageComponentProps,
		IModalContext,
		INotificationServiceComponentProps<ISystemAutomationTemplate> {
	className?: string;
	styles?: StyleDeclarationValue[];
}

interface IState {
	draftUpdated?: boolean;
	selectedIndustry?: Industry;
	selectedIndustryOptions?: ISelectOption<TemplateIndustryOption>[];
}

class _EditIndustryAutomationTemplate extends React.Component<IProps, IState> {
	private mIndustryAutomationTemplate: IndustryAutomationTemplateViewModel;

	constructor(props: IProps) {
		super(props);

		makeObservable<_EditIndustryAutomationTemplate, 'mIndustryAutomationTemplate'>(this, {
			mIndustryAutomationTemplate: observable.ref,
		});

		this.state = {
			selectedIndustryOptions: [DefaultIndustryDropdownOptions.find(x => x.dataContext === 'All')],
		};
	}

	public componentDidMount() {
		const { history, location } = this.props;

		const locationState: ILocationState<IndustryAutomationTemplateViewModel, any> = history?.location?.state;
		if (locationState?.viewModel) {
			const queryParams = queryStringToObject(location?.search || '');
			this.mIndustryAutomationTemplate = locationState.viewModel;
			const industries = new Set<Industry>(this.mIndustryAutomationTemplate.industries || []);
			const selectedIndustryOptions = DefaultIndustryDropdownOptions.filter(x =>
				industries.has(x.dataContext as Industry)
			);
			this.setState({
				selectedIndustry: queryParams?.industry,
				selectedIndustryOptions,
			});
		}
	}

	closeModal() {
		this.props.parentModal?.onRequestClose(null, false);
	}

	public render() {
		const { className, styles, parentModal, ...restProps } = this.props;
		const { selectedIndustry } = this.state;
		return (
			<div
				className={`${css(styleSheet.container, ...(styles || []))} edit-industry-automation-template ${
					className || ''
				}`}
			>
				{!!this.mIndustryAutomationTemplate && (
					<EditAutomationTemplate
						{...restProps}
						automationTemplate={this.mIndustryAutomationTemplate?.automationTemplate}
						onDraftUpdated={this.onDraftUpdated}
						onRenderHeader={this.onRenderHeader}
						readonly={
							selectedIndustry
								? this.mIndustryAutomationTemplate?.getStatusForIndustry(selectedIndustry) === TemplateStatus.Archived
								: undefined
						}
						onAutomationDeleted={this.closeModal}
						onAutomationPublished={this.closeModal}
						onAutomationDisabled={this.closeModal}
					/>
				)}
			</div>
		);
	}

	private onRenderHeader = () => {
		const { selectedIndustryOptions } = this.state;
		return (
			<Observer>
				{() => {
					return (
						<>
							<IndustriesDropdown
								onSelectedIndustryOptionsChanged={this.onSelectedIndustryOptionsChanged}
								selectedIndustryOptions={selectedIndustryOptions}
							/>
							{this.renderIndustryActions()}
						</>
					);
				}}
			</Observer>
		);
	};

	private renderIndustryActions = () => {
		const { selectedIndustry, draftUpdated } = this.state;
		if (this.mIndustryAutomationTemplate?.automationTemplate) {
			const status = selectedIndustry
				? this.mIndustryAutomationTemplate.getStatusForIndustry(selectedIndustry)
				: TemplateStatus.Draft;
			const nodes: React.ReactNode[] = [];
			const publishButton = (
				<button className={css(styleSheet.publishButton)} key='publish' onClick={this.publish}>
					<span>Publish</span>
				</button>
			);
			switch (status) {
				case TemplateStatus.Published: {
					if (this.mIndustryAutomationTemplate.automationTemplate.hasDraftVersion || !!draftUpdated) {
						nodes.push(publishButton);
					}
					nodes.push(
						<button className={css(baseStyleSheet.ctaButton)} key='archive' onClick={this.archive}>
							<span>Archive</span>
						</button>
					);
					break;
				}
				case TemplateStatus.Draft: {
					nodes.push(publishButton);
					break;
				}
				default: {
					break;
				}
			}
			return nodes;
		}
		return null;
	};

	private onDraftUpdated = () => {
		if (this.state.draftUpdated !== true) {
			this.setState({
				draftUpdated: true,
			});
		}
	};

	private publish = () => {
		const { selectedIndustry } = this.state;
		const { logApiError, logEvent, errorMessages, parentModal, postNotification } = this.props;

		let promise: Promise<any> = null;
		if (selectedIndustry) {
			promise = this.mIndustryAutomationTemplate.publish(selectedIndustry);
			if (promise) {
				logEvent('Publish', {
					industry: selectedIndustry,
					templateId: this.mIndustryAutomationTemplate.automationTemplate.id,
				});
				promise.catch((error: IOperationResultNoValue) => {
					errorMessages?.pushApiError(error);

					logApiError('Publish-Error', error);
				});
			}
		} else {
			// publish for all
			promise = this.setIndustries(this.mIndustryAutomationTemplate.industries, true);
		}
		promise?.then(() => {
			this.setState(
				{
					draftUpdated: false,
				},
				() => {
					postNotification({
						info: this.mIndustryAutomationTemplate.toJs(),
						topic: Topics.EDIT_SYSTEM_AUTOMATION_TEMPLATE,
					});
					parentModal?.onRequestClose(null, false);
				}
			);
		});
	};

	private archive = () => {
		const { selectedIndustry } = this.state;
		const { logApiError, logEvent, errorMessages, parentModal, postNotification } = this.props;
		if (selectedIndustry) {
			const promise = this.mIndustryAutomationTemplate.archive(selectedIndustry);
			if (promise) {
				logEvent('Archive', {
					industry: selectedIndustry,
					templateId: this.mIndustryAutomationTemplate.automationTemplate.id,
				});
				promise
					.then(() => {
						postNotification({
							info: this.mIndustryAutomationTemplate.toJs(),
							topic: Topics.EDIT_SYSTEM_AUTOMATION_TEMPLATE,
						});
						parentModal?.onRequestClose(null, false);
					})
					.catch((error: IOperationResultNoValue) => {
						errorMessages?.pushApiError(error);

						logApiError('Archive-Error', error);
					});
			}
		}
	};

	private onSelectedIndustryOptionsChanged = (nextSelectedIndustryOptions: ISelectOption<TemplateIndustryOption>[]) => {
		const { selectedIndustryOptions } = this.state;
		const industries =
			nextSelectedIndustryOptions[0].dataContext === 'All'
				? DefaultSupportedIndustries
				: nextSelectedIndustryOptions.map(x => x.dataContext as Industry);
		this.setState({ selectedIndustryOptions: nextSelectedIndustryOptions });
		this.setIndustries(industries)?.catch(() => this.setState({ selectedIndustryOptions }));
	};

	private setIndustries = (industries: Industry[], publish = false) => {
		const { logApiError, logEvent, errorMessages } = this.props;
		const promise = this.mIndustryAutomationTemplate.setIndustries(
			industries,
			publish ? TemplateStatus.Published : TemplateStatus.Draft
		);
		if (promise) {
			const eventName = publish ? 'PublishForIndustries' : 'SetIndustries';

			logEvent(eventName, { industries });
			promise.catch((error: IOperationResultNoValue) => {
				errorMessages?.pushApiError(error);

				logApiError(`${eventName}-Error`, error);
			});
		}
		return promise;
	};
}

const EditIndustryAutomationTemplateAsObserver = observer(_EditIndustryAutomationTemplate);
const EditIndustryAutomationTemplateWithContext = inject(
	ErrorMessagesViewModelKey,
	ModalChildComponentContextKey
)(EditIndustryAutomationTemplateAsObserver);
const EditIndustryAutomationTemplateWithNotificationService = withNotificationService(
	EditIndustryAutomationTemplateWithContext
);
export const EditIndustryAutomationTemplate = withEventLogging(
	EditIndustryAutomationTemplateWithNotificationService,
	'EditIndustryAutomationTemplate'
);
